// Shared TypeScript types for the Bleums Creations frontend.
// Kept thin at M1 — fill in as backend endpoints come online.

// ---------- Auth (admin) ----------

export interface AuthState {
  isAuthenticated: boolean;
  token?: string;
  user?: AdminUser;
}

export interface AdminUser {
  username: string;
  email?: string;
  groups?: string[];
}

// ---------- Money ----------

export interface Money {
  currency: string; // ISO 4217 — "USD"
  amount_cents: number;
}

// ---------- Products & Variants (M2) ----------

export interface Variant {
  sku: string;
  size?: string;
  color?: string;
  price_cents: number;
  weight_oz: number;
  image_id?: string;
  in_stock: boolean;
}

export interface Product {
  product_id: string;
  name: string;
  slug?: string;
  description: string;
  category?: string;
  status: 'active' | 'draft' | 'archived';
  images: string[]; // CloudFront URLs

  // Single product-level price and weight cover any combination of options.
  price_cents: number;
  weight_oz: number;
  in_stock: boolean;

  // Customer picks one of each at order time.
  size_options?: string[];
  color_options?: string[];
  thread_color_options?: string[];
  font_options?: string[];

  // Optional per-size price override (cents). Sizes not present use price_cents.
  size_price_overrides?: Record<string, number>;
  // Optional per-image color tag. Used to filter the customer carousel
  // when a color is selected.
  image_color_map?: Record<string, string>;

  // Legacy: per-SKU variants. Retained on the model for compatibility but
  // no longer surfaced in the admin editor.
  variants: Variant[];

  // Personalization fields a customer can fill in per cart line:
  personalization_fields?: PersonalizationField[];
  package_override?: PackageDims;
  // If set, product is only visible inside the named private library
  // (e.g. a school order form). Falsy => public catalog.
  library_slug?: string;
  created_at: string;
  updated_at?: string;
}

// ---------- Featured items (home carousel) ----------
//
// Past work shown on the home page. Stored as plain images in S3 under
// featured-items/, so the S3 key doubles as the identifier.

export interface FeaturedImage {
  key: string;
  url: string;
  filename: string;
  size: number;
  uploaded_at: string;
}

// ---------- Private libraries -------------------------------------------

export interface PrivateLibrary {
  slug: string;
  name: string;
  description?: string;
  // Hidden collections keep their products, password and URL — they just
  // stop being offered until they're needed again.
  visible?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface PrivateLibraryUnlockResponse {
  token: string;
  expires_in: number;
  library: PrivateLibrary;
}

export interface PersonalizationField {
  key: string;
  label: string;
  type: 'text' | 'select';
  required?: boolean;
  max_length?: number;
  options?: string[]; // for type='select'
}

export interface PackageDims {
  length_in: number;
  width_in: number;
  height_in: number;
}

export interface InventoryRow {
  sku: string;
  in_stock: boolean;
}

// ---------- Cart (M3, client-side only) ----------

export interface CartLinePersonalization {
  [key: string]: string;
}

export interface CartLine {
  product_id: string;
  product_name: string;
  product_image?: string;
  sku: string;
  variant_label: string; // e.g. "Size M / Sage / Cursive font"
  unit_price_cents: number;
  qty: number;
  personalization?: CartLinePersonalization;
  // What the customer typed about this piece: names, thread colours,
  // anything the option dropdowns don't cover.
  notes?: string;
}

// ---------- Checkout & Orders (M3) ----------

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string; // ISO — "US"
}

export interface CustomerInfo {
  email: string;
  name: string;
  phone?: string;
  shipping_address: Address;
  billing_address?: Address; // defaults to shipping
}

export type ShippingMethod = 'standard' | 'pickup';

// ---------- Payment methods ----------
//
// The shop configures its accepted methods once in settings; each invoice
// then picks a subset. 'stripe' is the card option — selecting it mints a
// Stripe Payment Link stored on the order as payment_link_url.

export interface PaymentMethodOption {
  key: string;
  label: string;
  details?: string;
  instructions?: string;
  // Shown beside the label on the invoice, and used as the logo's alt text
  // so a blocked or unprinted image still leaves a mark behind.
  emoji?: string;
  // Uploaded brand logo, used instead of the emoji when present.
  logo_url?: string;
}

export const STRIPE_METHOD_KEY = 'stripe';

// ---------- Storefront order requests ----------
//
// The storefront doesn't charge cards. Submitting the cart creates a pending
// order the shop reviews and then invoices. Note that no price is sent: the
// backend re-prices every line from the catalog.

export interface OrderRequestItem {
  product_id: string;
  qty: number;
  personalization?: CartLinePersonalization;
  notes?: string;
}

export interface OrderRequestSubmission {
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  fulfillment: FulfillmentMode;
  shipping_address?: Address;
  items: OrderRequestItem[];
  customer_notes?: string;
}

export interface OrderRequestResponse {
  order_id: string;
  order_number: string;
  customer_email: string;
  subtotal_cents: number;
  total_cents: number;
}

export type OrderStatus =
  | 'created'
  | 'invoice_sent'
  | 'paid'
  | 'in_progress'
  | 'ready_for_pickup'
  | 'shipped'
  | 'completed'
  | 'cancelled'
  | 'refunded';

export type ItemSource = 'customer_provided' | 'shop_provided' | 'unsure' | 'na';
export type FulfillmentMode = 'pickup' | 'shipping';
// What an admin can set on an order: the storefront's two modes, plus a
// local drop-off by the shop, and "none" to leave fulfillment off the
// invoice entirely.
export type OrderFulfillment = FulfillmentMode | 'dropoff' | 'none';

export interface OrderLine {
  product_id?: string;
  product_name: string;
  description?: string;
  qty: number;
  unit_price_cents: number;
  line_total_cents: number;
  item_source: ItemSource;
  personalization?: Record<string, string>;
  notes?: string;
}

export interface OrderLineInput {
  product_id?: string;
  product_name: string;
  description?: string;
  qty: number;
  unit_price_cents: number;
  item_source?: ItemSource;
  personalization?: Record<string, string>;
  notes?: string;
}

export interface EmailLogEntry {
  kind: string;
  sent: boolean;
  at: string;
  to?: string;
  subject?: string;
  reason?: string;
}

export interface Order {
  order_id: string;
  order_number: string;
  status: OrderStatus;
  // Absent on orders taken in person, where a phone number is all the shop
  // has. Anything that emails has to check for it first.
  customer_email?: string;
  customer_name: string;
  customer_phone?: string;
  fulfillment: OrderFulfillment;
  shipping_address?: Address;
  lines: OrderLine[];
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  discount_cents: number;
  total_cents: number;
  tracking_number?: string;
  carrier?: string;
  // What the customer has actually been sent, newest last.
  email_log?: EmailLogEntry[];
  // Silences every customer email for this order.
  suppress_emails?: boolean;
  // Overrides the shop-wide send_payment_receipts setting.
  send_payment_receipt?: boolean;
  internal_notes?: string;
  // Customer-visible note printed on the invoice (e.g. "local pickup").
  description?: string;
  due_date?: string;
  // Percentage applied to the discounted subtotal to derive tax_cents.
  tax_rate_percent?: number;
  invoice_number?: number;
  invoice_date?: string;
  invoice_token?: string;
  payment_method?: string;
  payment_link_url?: string;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  invoice_sample_image_url?: string;
  // Payment method keys this invoice accepts (see PaymentMethodOption).
  accepted_payment_methods?: string[];
  inquiry_id?: string;
  // 'admin' for orders keyed in by the shop, 'storefront' for customer
  // cart submissions awaiting review.
  source?: 'admin' | 'storefront';
  customer_notes?: string;
  created_at: string;
  updated_at?: string;
  // First time the order reached each milestone. Separate from status,
  // which only says where it is now — the shop often makes an item
  // before the invoice goes out.
  invoice_sent_at?: string;
  paid_at?: string;
  in_progress_at?: string;
  ready_at?: string;
  shipped_at?: string;
  completed_at?: string;
}

export interface OrderCreatePayload {
  customer_name: string;
  // Optional here, unlike the storefront's own OrderRequestSubmission: an
  // admin often takes an order with only a name and a phone number.
  customer_email?: string;
  customer_phone?: string;
  description?: string;
  tax_rate_percent?: number;
  fulfillment?: OrderFulfillment;
  shipping_address?: Address;
  lines: OrderLineInput[];
  shipping_cents?: number;
  tax_cents?: number;
  discount_cents?: number;
  internal_notes?: string;
  due_date?: string;
  invoice_date?: string;
  // '' clears an existing proof image; undefined leaves it alone.
  invoice_sample_image_url?: string;
  payment_method?: string;
  status?: OrderStatus;
  inquiry_id?: string;
  suppress_emails?: boolean;
  send_payment_receipt?: boolean;
}

export interface OrderUpdatePayload {
  status?: OrderStatus;
  description?: string;
  tax_rate_percent?: number;
  tracking_number?: string;
  carrier?: string;
  // What the customer has actually been sent, newest last.
  email_log?: EmailLogEntry[];
  // Silences every customer email for this order.
  suppress_emails?: boolean;
  // Overrides the shop-wide send_payment_receipts setting.
  send_payment_receipt?: boolean;
  internal_notes?: string;
  due_date?: string;
  payment_method?: string;
  fulfillment?: OrderFulfillment;
  shipping_address?: Address;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  lines?: OrderLineInput[];
  shipping_cents?: number;
  tax_cents?: number;
  discount_cents?: number;
}

// ---------- Inquiries (M4) ----------

export type InquiryStatus = 'new' | 'reviewed' | 'quoted' | 'declined' | 'converted';

export interface InquiryCreate {
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  message: string;
  item_source?: ItemSource;
  image_file_ids?: string[];
  fulfillment?: FulfillmentMode;
  shipping_address?: Address;
}

export interface Inquiry {
  inquiry_id: string;
  status: InquiryStatus;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  message: string;
  item_source: ItemSource;
  image_file_ids: string[];
  // Resolved from image_file_ids by the API — the ids alone can't be
  // rendered, and the photos are the point of most custom requests.
  image_urls?: string[];
  fulfillment: FulfillmentMode;
  shipping_address?: Address;
  internal_notes?: string;
  converted_order_id?: string;
  created_at: string;
  updated_at?: string;
}
